// Genres des lignes TRK (camions et remorques indépendants) : lignes de chaque onglet, filtre par partie, champs du dompeur.

export function truckUnitKind(truck) {
  return truck?.unit_kind || "ensemble";
}

export function isDompeurTruck(truck) {
  return String(truck?.truck_type || "").trim().toLowerCase() === "dompeur";
}

// « Type » de Camions : Citerne ou Dompeur ; l'attelage se lit dans « Remorque habituelle ».
export function truckTypeLabel(truck) {
  return isDompeurTruck(truck) ? "Dompeur" : "Citerne";
}

export const tonsLabel = (value) => Number(value || 0).toLocaleString("fr-CA", { maximumFractionDigits: 2 });

// « Changer le type ? » : ce que le changement efface, sinon "" (un camion seul sans compartiments devient dompeur sans
// rien effacer). Un dompeur redevient toujours un camion sans remorque, même un ancien ensemble.
export function truckTypeChangeNotice(truck, draft) {
  if (isDompeurTruck(truck) === isDompeurTruck(draft)) return "";
  const name = truck?.truck_name || truck?.truck_id;
  const trailer = truck?.compartment_name;
  const count = truck?.compartments?.length || 0;
  const compartments = count > 1 ? `ses ${count} compartiments` : "son compartiment";
  if (!isDompeurTruck(truck)) {
    // Décision H4 : un dompeur n'a pas de compartiments ; ceux de la remorque sont effacés.
    const leaves = truckUnitKind(truck) === "ensemble" && trailer ? `${trailer} quitte Remorques : ${name}-${trailer} devient un dompeur d'une seule pièce` : "";
    if (!count) return leaves ? `${leaves}.` : "";
    return `${leaves || `${name} devient un dompeur`} ; ${compartments} ${count > 1 ? "sont effacés" : "est effacé"}.`;
  }
  const erased = [trailer && `son nom de remorque ${trailer}`, count && compartments].filter(Boolean);
  return `${name} devient un camion sans remorque ; sa capacité de ${tonsLabel(truck?.capacity_tons)} t est retirée`
    + `${erased.length ? `, ainsi que ${erased.join(" et ")}` : ""}.`;
}

// Camions : ensembles, dompeurs et camions seuls. Remorques : ensembles et remorques seules, jamais un dompeur.
export function isTruckInTab(truck, tab) {
  if (tab === "compartments") return truckUnitKind(truck) !== "camion" && !isDompeurTruck(truck);
  return truckUnitKind(truck) !== "remorque";
}

export function localDateKey(value = new Date()) {
  return value.toLocaleDateString("en-CA");
}

// Par partie : dans Camions, le camion (un dompeur et un camion seul suivent leur date) ; dans Remorques, la remorque.
export function retirementDateInTab(truck, tab) {
  const retiredOn = tab === "compartments" ? truck?.trailer_retired_on : truck?.tractor_retired_on;
  return retiredOn ? String(retiredOn).slice(0, 10) : "";
}

// « Retirés » à partir de la date ; avant, la ligne reste dans la flotte avec sa date de retrait affichée.
export function isRetiredInTab(truck, tab, today = localDateKey()) {
  const retiredOn = retirementDateInTab(truck, tab);
  return Boolean(retiredOn) && retiredOn <= today;
}

export function fleetFilterCounts(trucks, tab, today = localDateKey()) {
  const tabTrucks = trucks.filter((truck) => isTruckInTab(truck, tab));
  const retired = tabTrucks.filter((truck) => isRetiredInTab(truck, tab, today)).length;
  return { fleet: tabTrucks.length - retired, retired };
}

export function trucksForTab(trucks, tab, fleetFilter = "fleet", today = localDateKey()) {
  return trucks.filter((truck) => isTruckInTab(truck, tab) && isRetiredInTab(truck, tab, today) === (fleetFilter === "retired"));
}

// Camions, « Supprimer » : une citerne avec sa remorque ne perd que son camion, sa remorque reste dans Remorques sans
// camion ; un dompeur et un camion seul sont supprimés (le serveur refuse celui qui a roulé).
export function truckSelectionDeletion(trucks = []) {
  const name = (truck) => truck?.truck_name || truck?.truck_id;
  const keepsTrailer = (truck) => truckUnitKind(truck) === "ensemble" && !isDompeurTruck(truck);
  if (trucks.length === 1) {
    const [truck] = trucks;
    return {
      title: `Supprimer le camion ${name(truck)} ?`,
      description: keepsTrailer(truck)
        ? `Sa remorque${truck.compartment_name ? ` ${truck.compartment_name}` : ""} reste dans Remorques, sans camion.`
        : "Ce camion est supprimé du Data Manager actif.",
      items: [],
    };
  }
  const kept = trucks.filter(keepsTrailer);
  const deleted = trucks.filter((truck) => !keepsTrailer(truck));
  return {
    title: "Supprimer la sélection de camions ?",
    description: "Une citerne avec sa remorque ne perd que son camion : sa remorque reste dans Remorques, sans camion.",
    items: [
      ...(kept.length ? [`Camions supprimés, remorques gardées : ${kept.map((truck) => `${name(truck)}${truck.compartment_name ? ` (${truck.compartment_name})` : ""}`).join(", ")}.`] : []),
      ...(deleted.length ? [`Camions supprimés : ${deleted.map(name).join(", ")}.`] : []),
    ],
  };
}

// Remorques, « Supprimer la sélection » : une remorque seule est supprimée avec ses compartiments (le serveur refuse
// celle qui a roulé) ; une remorque d'un ensemble perd seulement ses compartiments.
export function trailerSelectionDeletion(trucks = []) {
  const trailers = trucks.filter((truck) => truckUnitKind(truck) === "remorque");
  const emptied = trucks.filter((truck) => truckUnitKind(truck) !== "remorque");
  const list = (items) => items.map((truck) => {
    const count = truck?.compartments?.length || 0;
    return `${truck.compartment_name || truck.truck_id} (${count ? `${count} compartiment${count > 1 ? "s" : ""}` : "aucun compartiment"})`;
  }).join(", ");
  return {
    trailers,
    emptied,
    items: [
      ...(trailers.length ? [`${trailers.length > 1 ? "Remorques seules supprimées" : "Remorque seule supprimée"} (aucune activité) : ${list(trailers)}.`] : []),
      ...(emptied.length ? [`Compartiments supprimés, ${emptied.length > 1 ? "les remorques restent" : "la remorque reste"} : ${list(emptied)}.`] : []),
    ],
  };
}
